import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/shared/hooks";
import type { DateRange, Locale } from "@/shared/utils";
import { useGetAdvancesQuery } from "../api";
import type { AdvancesFilters, AdvanceStatus, AdvanceListType } from "../types";

const year = new Date().getFullYear();
const DEFAULT_DATE_FROM = `${year}-01-01`;
const DEFAULT_DATE_TO = `${year}-12-31`;

export interface UseAdvancesListPageReturn {
  data: import("../types").AdvancesListResponse | undefined;
  items: import("../types").AdvanceListItem[];
  isLoading: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  typeFilter: AdvanceListType | null;
  setTypeFilter: (value: AdvanceListType | null) => void;
  statusFilter: AdvanceStatus | null;
  setStatusFilter: (value: AdvanceStatus | null) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  activeFilters: boolean;
  handleResetFilters: () => void;
  locale: Locale;
  pageItems: import("../types").AdvanceListItem[];
  pagination: ReturnType<typeof usePagination>;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useAdvancesListPage(): UseAdvancesListPageReturn {
  const { i18n, t } = useTranslation("AdvancesPage");
  const locale = i18n.language as Locale;

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<AdvanceListType | null>(null);
  const [statusFilter, setStatusFilter] = useState<AdvanceStatus | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });

  const filters: Partial<AdvancesFilters> = useMemo(
    () => ({
      search: search || undefined,
      type: typeFilter ?? undefined,
      status: statusFilter ?? undefined,
      dateFrom: dateRange.start
        ? `${dateRange.start.getFullYear()}-${String(dateRange.start.getMonth() + 1).padStart(2, "0")}-${String(dateRange.start.getDate()).padStart(2, "0")}`
        : DEFAULT_DATE_FROM,
      dateTo: dateRange.end
        ? `${dateRange.end.getFullYear()}-${String(dateRange.end.getMonth() + 1).padStart(2, "0")}-${String(dateRange.end.getDate()).padStart(2, "0")}`
        : DEFAULT_DATE_TO,
    }),
    [search, typeFilter, statusFilter, dateRange],
  );

  const { data, isLoading, isError } = useGetAdvancesQuery(filters);

  const items = useMemo(() => {
    if (!data) return [];
    return data.results.filter((item) => !(item.kind === "salary" && item.status === "draft"));
  }, [data]);

  const activeFilters = useMemo(() => {
    return !!search.trim() || !!typeFilter || !!statusFilter || !!dateRange.start || !!dateRange.end;
  }, [search, typeFilter, statusFilter, dateRange]);

  const pagination = usePagination(items.length, 10, [search, typeFilter, statusFilter, dateRange]);
  const pageItems = items.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setTypeFilter(null);
    setStatusFilter(null);
    setDateRange({ start: null, end: null });
  }

  return {
    data,
    items,
    isLoading,
    isError,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    activeFilters,
    handleResetFilters,
    locale,
    pageItems,
    pagination,
    t,
  };
}
